/**
 * Parsed tool call types with well-typed arguments extracted from ProgressToolCall.args.
 *
 * Any agent can implement parsing for its tools here so that rendering code
 * gets structured access to tool-specific fields instead of raw args casts.
 */

import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolType,
  MCP_TOOL_NAME_SEPARATOR,
  isMCPTool,
  isToolType,
  parseMCPToolName,
} from 'llm/chat-library/tool-details/types';
import {coerceToString} from 'util/coerceToString';
import {assertNever} from 'util/Types';

// ============================================================================
// Base and MCP interfaces
// ============================================================================

export interface BaseParsedProgressToolCall extends ProgressToolCall {
  tool: ToolType | string;
}

export interface MCPToolCall extends ProgressToolCall {
  tool: `${string}${typeof MCP_TOOL_NAME_SEPARATOR}${string}`;
  serverName: string;
  originalToolName: string;
  args: Record<string, unknown>;
}

// ============================================================================
// Typed tool call interfaces
// ============================================================================

export interface SearchToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchTool;
  query: string;
  objectTypes: Array<string>;
}

export interface GetWarehouseOverviewToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.GetWarehouseOverviewTool;
}

export interface QueryWarehouseToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.QueryWarehouseTool;
  query: string;
}

export interface SummarizeDocumentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SummarizeDocument;
  documentId: string;
}

export interface AttachmentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.AttachmentTool;
  attachmentId: string;
}

export interface ReadBlobToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ReadBlob;
  blobId: string;
}

export interface PublicDataSubAgentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.PublicDataSubAgent;
  query: string;
}

export interface WebSearchToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.WebSearch;
  query: string;
}

export interface SearchNotebookEntriesToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchNotebookEntries;
  queries: Array<string>;
  authoredBy?: Array<string>;
  createdAfter?: string;
  createdBefore?: string;
  modifiedAfter?: string;
  modifiedBefore?: string;
  reviewCompletedAfter?: string;
  reviewCompletedBefore?: string;
  reviewStatus?: string;
  reviewRole?: string;
  inProjectOrFolder?: string;
  inStudy?: string;
  offset: number;
}

export interface SearchSequencesToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchSequencesTool;
  query: string;
}

export interface SearchChemicalsToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SearchChemicalsTool;
  query: string;
  structure?: string;
  searchMode?: string;
}

export interface IdsToModelsToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.IdsToModelsTool;
  objectIds: Array<string>;
  includeRelevantItems: boolean;
}

export interface PlottingToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.PlottingTool;
  code: string;
}

export interface GenerateImageToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.GenerateImageTool;
  prompt: string;
}

export interface CustomCodeToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CustomCodeTool;
  code: string;
}

export interface ExecuteCustomCodePipelineStepToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ExecuteCustomCodePipelineStep;
  code: string;
}

export interface RunCodeToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.RunCodeTool;
  code: string;
}

export interface ToolSearchToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ToolSearch;
  query: string;
}

export interface SleepToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.SleepTool;
  seconds: number;
}

export interface ThinkToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.Think;
}

export interface ResearchSubAgentToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ResearchSubAgent;
  prompt: string;
  promptSummary?: string;
}

export interface CouncilToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CouncilTool;
  prompt: string;
}

export interface CompleteAnswerToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CompleteAnswer;
}

export interface CompleteAnswerToolToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CompleteAnswerTool;
}

export interface DeepResearchToolToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.DeepResearchTool;
}

export interface LoadSkillToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.LoadSkill;
  skillId: string;
  skillName: string;
  filePath: string;
}

/**
 * Content source for a ManageSkill file (SKILL.md or a supporting file): the content is provided
 * either inline as `text` or by `artifactId` (an artifact whose text is the content). Exactly one
 * is non-null; `getManageSkillFiles` and the preview resolve `artifactId` to text before rendering.
 */
export interface SkillFileSource {
  text: string | null;
  artifactId: string | null;
}

export interface ManageSkillToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ManageSkill;
  action: 'create' | 'edit';
  /** Inline SKILL.md text; empty string when the SKILL.md content is provided by artifact id. */
  skillContent: string;
  /** Artifact id whose text is the SKILL.md content, or null when provided inline. */
  skillContentArtifactId: string | null;
  skillFiles: Record<string, SkillFileSource>;
  skillId: string | null;
}

export interface CreateAnalysisPlanToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CreateAnalysisPlan;
}

export interface CreateProcedureRunPlanToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.CreateProcedureRunPlan;
}

export interface ValidateProcedureRunPlanToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.ValidateProcedureRunPlan;
}

export interface PromoteProcedureRunPlanToolCall extends BaseParsedProgressToolCall {
  tool: ToolType.PromoteProcedureRunPlan;
}

/**
 * Union of all parsed tool call types.
 * Each member has a unique literal `tool` discriminant, enabling TypeScript
 * to narrow the type in switch statements without explicit casts.
 */
export type ParsedProgressToolCall =
  | SearchToolCall
  | GetWarehouseOverviewToolCall
  | QueryWarehouseToolCall
  | SummarizeDocumentToolCall
  | SearchNotebookEntriesToolCall
  | SearchSequencesToolCall
  | SearchChemicalsToolCall
  | AttachmentToolCall
  | ReadBlobToolCall
  | PublicDataSubAgentToolCall
  | WebSearchToolCall
  | IdsToModelsToolCall
  | PlottingToolCall
  | GenerateImageToolCall
  | CustomCodeToolCall
  | ExecuteCustomCodePipelineStepToolCall
  | RunCodeToolCall
  | ToolSearchToolCall
  | SleepToolCall
  | ThinkToolCall
  | ResearchSubAgentToolCall
  | CouncilToolCall
  | CompleteAnswerToolCall
  | CompleteAnswerToolToolCall
  | DeepResearchToolToolCall
  | LoadSkillToolCall
  | ManageSkillToolCall
  | CreateAnalysisPlanToolCall
  | CreateProcedureRunPlanToolCall
  | ValidateProcedureRunPlanToolCall
  | PromoteProcedureRunPlanToolCall
  | MCPToolCall;

// ============================================================================
// Parser
// ============================================================================

function toStringArray(value: unknown): Array<string> {
  if (Array.isArray(value)) {
    return value.map((v) => coerceToString(v));
  }
  if (typeof value === 'string' && value.length > 0) {
    return [value];
  }
  return [];
}

/**
 * Coerce a tool-call arg to string, preserving undefined for absent fields.
 * Use for optional display fields where `undefined` and `''` should remain
 * distinguishable.
 */
function coerceOptionalString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  return coerceToString(value);
}

/**
 * Coerce a tool-call arg to a boolean. Accepts native booleans and the
 * common LLM-string variants ('true'/'false', case-insensitive). Anything
 * else falls back to the supplied default — typical pattern for tool args
 * that are nominally booleans but sometimes arrive stringly-typed from
 * model output.
 */
function coerceBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true') {
      return true;
    }
    if (lower === 'false') {
      return false;
    }
  }
  return fallback;
}

/**
 * Coerce a tool-call arg to a number. Accepts native numbers and finite
 * numeric strings; falls back otherwise. Same rationale as `coerceBoolean`.
 */
function coerceNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

/**
 * Parse a single ManageSkill file source arg into a {@link SkillFileSource}.
 *
 * The current tool schema sends `{text}` or `{artifact_id}` objects. A bare string is also
 * accepted and treated as inline text, so historical tool calls (which sent path -> string) still
 * render.
 */
function parseSkillFileSource(value: unknown): SkillFileSource {
  if (typeof value === 'string') {
    return {text: value, artifactId: null};
  }
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    return {
      text: typeof obj.text === 'string' ? obj.text : null,
      artifactId: typeof obj.artifact_id === 'string' ? obj.artifact_id : null,
    };
  }
  return {text: null, artifactId: null};
}

function parseSkillFileSources(raw: unknown): Record<string, SkillFileSource> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const result: Record<string, SkillFileSource> = {};
  for (const [path, value] of Object.entries(raw as Record<string, unknown>)) {
    result[path] = parseSkillFileSource(value);
  }
  return result;
}

/**
 * Parse a generic ProgressToolCall into a typed ParsedProgressToolCall.
 * Returns null for tool types that don't have a known parser.
 */
export function parseProgressToolCall(toolCall: ProgressToolCall): ParsedProgressToolCall | null {
  const toolName = toolCall.tool;

  if (isMCPTool(toolName)) {
    const {serverName, originalToolName} = parseMCPToolName(toolName);
    return {
      ...toolCall,
      tool: toolName as MCPToolCall['tool'],
      serverName,
      originalToolName,
      args: toolCall.args ?? {},
    };
  }

  if (!isToolType(toolName)) {
    return null;
  }

  const args = toolCall.args ?? {};

  switch (toolName) {
    case ToolType.SearchTool:
      return {
        ...toolCall,
        tool: ToolType.SearchTool,
        query: coerceToString(args.query),
        objectTypes: toStringArray(args.objectTypes),
      };
    case ToolType.SearchNotebookEntries:
      return {
        ...toolCall,
        tool: ToolType.SearchNotebookEntries,
        queries: toStringArray(args.search_queries),
        authoredBy: args.authored_by == null ? undefined : toStringArray(args.authored_by),
        createdAfter: coerceOptionalString(args.created_after),
        createdBefore: coerceOptionalString(args.created_before),
        modifiedAfter: coerceOptionalString(args.modified_after),
        modifiedBefore: coerceOptionalString(args.modified_before),
        reviewCompletedAfter: coerceOptionalString(args.review_completed_after),
        reviewCompletedBefore: coerceOptionalString(args.review_completed_before),
        reviewStatus: coerceOptionalString(args.review_status),
        reviewRole: coerceOptionalString(args.review_role),
        inProjectOrFolder: coerceOptionalString(args.in_project_or_folder),
        inStudy: coerceOptionalString(args.in_study),
        offset: coerceNumber(args.offset, 0),
      };
    case ToolType.SearchSequencesTool:
      return {...toolCall, tool: ToolType.SearchSequencesTool, query: coerceToString(args.query)};
    case ToolType.SearchChemicalsTool:
      return {
        ...toolCall,
        tool: ToolType.SearchChemicalsTool,
        query: coerceToString(args.query),
        structure: coerceOptionalString(args.structure),
        searchMode: coerceOptionalString(args.search_mode),
      };
    case ToolType.GetWarehouseOverviewTool:
      return {...toolCall, tool: ToolType.GetWarehouseOverviewTool};
    case ToolType.QueryWarehouseTool:
      return {...toolCall, tool: ToolType.QueryWarehouseTool, query: coerceToString(args.query)};
    case ToolType.SummarizeDocument:
    case ToolType.ReadBenchlingDocumentLegacy:
      return {
        ...toolCall,
        tool: ToolType.SummarizeDocument,
        documentId: coerceToString(args.document_id),
      };
    case ToolType.AttachmentTool:
      return {
        ...toolCall,
        tool: ToolType.AttachmentTool,
        attachmentId: coerceToString(args.attachmentId),
      };
    case ToolType.ReadBlob:
      return {
        ...toolCall,
        tool: ToolType.ReadBlob,
        blobId: coerceToString(args.blob_id),
      };
    case ToolType.PublicDataSubAgent:
      return {...toolCall, tool: ToolType.PublicDataSubAgent, query: coerceToString(args.query)};
    case ToolType.WebSearch:
      return {...toolCall, tool: ToolType.WebSearch, query: coerceToString(args.query)};
    case ToolType.IdsToModelsTool:
      return {
        ...toolCall,
        tool: ToolType.IdsToModelsTool,
        objectIds: toStringArray(args.object_ids),
        includeRelevantItems: coerceBoolean(args.include_relevant_items, false),
      };
    case ToolType.PlottingTool:
      return {...toolCall, tool: ToolType.PlottingTool, code: coerceToString(args.code)};
    case ToolType.GenerateImageTool:
      return {...toolCall, tool: ToolType.GenerateImageTool, prompt: coerceToString(args.prompt)};
    case ToolType.CustomCodeTool:
      return {
        ...toolCall,
        tool: ToolType.CustomCodeTool,
        code: coerceToString(args.code),
      };
    case ToolType.ExecuteCustomCodePipelineStep:
      return {
        ...toolCall,
        tool: ToolType.ExecuteCustomCodePipelineStep,
        code: coerceToString(args.code),
      };
    case ToolType.RunCodeTool:
      return {
        ...toolCall,
        tool: ToolType.RunCodeTool,
        code: coerceToString(args.code),
      };
    case ToolType.ToolSearch:
      return {...toolCall, tool: ToolType.ToolSearch, query: coerceToString(args.query)};
    case ToolType.SleepTool:
      return {...toolCall, tool: ToolType.SleepTool, seconds: coerceNumber(args.seconds, 0)};
    case ToolType.Think:
      return {...toolCall, tool: ToolType.Think};
    case ToolType.ResearchSubAgent:
      return {
        ...toolCall,
        tool: ToolType.ResearchSubAgent,
        prompt: coerceToString(args.prompt),
        promptSummary: coerceOptionalString(args.prompt_summary),
      };
    case ToolType.CouncilTool:
      return {
        ...toolCall,
        tool: ToolType.CouncilTool,
        prompt: coerceToString(args.prompt),
      };
    case ToolType.CompleteAnswer:
      return {...toolCall, tool: ToolType.CompleteAnswer};
    case ToolType.CompleteAnswerTool:
      return {...toolCall, tool: ToolType.CompleteAnswerTool};
    case ToolType.DeepResearchTool:
      return {...toolCall, tool: ToolType.DeepResearchTool};
    case ToolType.LoadSkill:
      return {
        ...toolCall,
        tool: ToolType.LoadSkill,
        skillId: coerceToString(args.skill_id),
        skillName: coerceToString(args.skill_name),
        filePath: coerceToString(args.file_path),
      };
    case ToolType.ManageSkill: {
      const rawAction = args.action;
      const action: 'create' | 'edit' = rawAction === 'create' || rawAction === 'edit' ? rawAction : 'create';
      return {
        ...toolCall,
        tool: ToolType.ManageSkill,
        action,
        skillContent: coerceToString(args.skill_content),
        skillContentArtifactId:
          typeof args.skill_content_artifact_id === 'string' ? args.skill_content_artifact_id : null,
        skillFiles: parseSkillFileSources(args.skill_files),
        skillId: typeof args.skill_id === 'string' ? args.skill_id : null,
      };
    }

    case ToolType.CreateAnalysisPlan:
      return {...toolCall, tool: ToolType.CreateAnalysisPlan};

    // N/A - this tool is not found in TOOLS_TO_DISPLAY.
    case ToolType.Respond:
      return null;

    // TODO: Parse these displayed tools to support input/output rendering in the activity modal.
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.AskQuestion:
    case ToolType.InstantiateDocument:
    case ToolType.SearchEntriesAndTemplates:
    case ToolType.SearchBenchlingObjects:
    case ToolType.TranscribeFile:
    case ToolType.ReviewTool:
    case ToolType.EditTableCells:
    case ToolType.UpdateDocumentContent:
    case ToolType.UpdateDocumentLegacy:
    case ToolType.InsertDocumentContent:
    case ToolType.SearchFiles:
    case ToolType.SearchSchemas:
    case ToolType.FillTables:
    case ToolType.ReadFiles:
    case ToolType.CreateLookupConfig:
    case ToolType.SaveArtifact:
    case ToolType.ListArtifacts:
    case ToolType.ViewArtifact:
    case ToolType.LoadFileAsArtifact:
    case ToolType.LoadDocumentAttachmentAsArtifact:
    case ToolType.SubstituteInArtifact:
    case ToolType.UploadFileToSandbox:
    case ToolType.LoadDropdownOptions:
    case ToolType.HelpDocs:
    case ToolType.LoadPackageDocs:
    case ToolType.AskWarehouse:
    // Co-scientist tools
    // falls through
    case ToolType.ListFolderContents:
    case ToolType.CreateFolder:
    case ToolType.CreateEntry:
    case ToolType.CreateFile:
    case ToolType.UpdateFile:
    case ToolType.ReadFile:
    case ToolType.CreateAASequences:
    case ToolType.CreateMolecule:
    case ToolType.CreateCustomEntities:
    case ToolType.UpdateCustomEntities:
    case ToolType.CreateWorkflowTaskGroups:
    case ToolType.UpdateWorkflowTaskGroups:
    case ToolType.CreateWorkflowTasks:
    case ToolType.UpdateWorkflowTasks:
    case ToolType.ExecuteWorkflowTasksIntoEntry:
    case ToolType.ArchiveItem:
    case ToolType.RenameItem:
    case ToolType.MoveItemToFolder:
    case ToolType.SetTargetEntry:
    case ToolType.AddExistingEntitiesToTable:
    case ToolType.AddExistingResultsToTable:
    case ToolType.CreateDocumentTool:
    case ToolType.CreateDraftDocument:
    case ToolType.InvokeToolsFromFile:
    case ToolType.RegisterEntities:
    case ToolType.TransferIntoContainers:
    case ToolType.UpdateContainerFields:
    case ToolType.UpdateContainerLocations:
    case ToolType.CreatePlates:
    case ToolType.CreateContainers:
    case ToolType.CreateResults:
    case ToolType.StartChatSession:
    case ToolType.GetChatSession:
    case ToolType.ListChatSessions:
    case ToolType.ScheduleForFuture:
    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
    case ToolType.UpdateProcedureRunPlanConditions:
    case ToolType.AddProcedureRunPlanConditions:
    case ToolType.DeleteProcedureRunPlanConditions:
    case ToolType.UpdateProcedureRunPlanConditionReplicates:
    case ToolType.CreateProcedureRunParameterValue:
    case ToolType.CreateLabAutomationRun:
    case ToolType.ExecuteLabAutomationRun:
    case ToolType.GenerateLegacyLabAutomationInput:
    case ToolType.ProcessLegacyLabAutomationOutput:
    case ToolType.GetLegacyLabAutomationOutputProcessingStatus:
    // Structure prediction tools
    // falls through
    case ToolType.ListStructurePredictionModels:
    case ToolType.StartStructurePredictionBatch:
    case ToolType.GetStructurePredictionBatchResults:
    // Snowflake gateway tools
    // falls through
    case ToolType.ListSnowflakeTables:
    case ToolType.ExportSnowflakeTableToDataset:
    case ToolType.GetSnowflakeExportStatus:
    // Canvas & Opentrons lab automation tools
    // falls through
    case ToolType.CreateCanvasFromHtml:
    case ToolType.UpdateCanvasHtml:
    case ToolType.UploadOpentronsProtocol:
    case ToolType.CreateOpentronsRun:
    case ToolType.StartOpentronsRun:
    // Analysis & automation template tools
    // falls through
    case ToolType.CreateAnalysis:
    case ToolType.ApplyTemplateToAnalysis:
    case ToolType.CreateAutomationTemplate:
    case ToolType.EditAutomationTemplate:
    case ToolType.EditAnalysis:
    // Schema administration tools
    // falls through
    case ToolType.CreateDropdownConfig:
    case ToolType.CreateCustomEntitySchema:
    case ToolType.CreateRunSchema:
    // Agent tools
    // falls through
    case ToolType.ManageRoutine:
    case ToolType.ManageAgent:
    case ToolType.RunAgentDirectly:
    // DNA sequence creation
    // falls through
    case ToolType.CreateDNASequences:
      return null;
    default:
      assertNever(toolName);
      return null;
  }
}
